import { useEffect, useState } from 'react';
import { Award, BadgePercent, CheckCircle2, DollarSign, Eye, ExternalLink, ShoppingBag, TicketCheck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useMlOrders } from '@/hooks/useMlOrders';
import { useMlMetrics, reputationLabel } from '@/hooks/useMlMetrics';
import { formatBRL } from '@/lib/format';
import { StatCard } from '@/components/ui/StatCard';
import { OrdersTable } from '@/components/logistics/OrdersTable';
import { SalesChart } from '@/components/logistics/SalesChart';
import { TopProducts } from '@/components/logistics/TopProducts';
import { InventoryAlerts } from '@/components/logistics/InventoryAlerts';
import { ConversionFunnel, CustomerService } from '@/components/logistics/ConversionAndService';

import { ML_CLIENT_ID, getMlRedirectUri } from '@/lib/ml';

function generateVerifier(): string {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function generateChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

async function connectML() {
  if (!ML_CLIENT_ID) {
    alert('Configuração ausente: VITE_ML_CLIENT_ID não está definido neste build. Configure a variável no ambiente de build (Cloudflare Pages → Settings → Environment variables) e faça novo deploy.');
    return;
  }
  const redirectUri = getMlRedirectUri();
  // Guarda a redirect_uri usada na autorização para reusar EXATAMENTE o mesmo
  // valor na troca do code (exigência do OAuth do ML).
  sessionStorage.setItem('ml_redirect_uri', redirectUri);

  const verifier = generateVerifier();
  const challenge = await generateChallenge(verifier);
  sessionStorage.setItem('ml_pkce_verifier', verifier);

  const url = new URL('https://auth.mercadolivre.com.br/authorization');
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', ML_CLIENT_ID);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('scope', 'offline_access read write');
  url.searchParams.set('code_challenge', challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  window.location.href = url.toString();
}

interface MlConnection {
  nickname: string | null;
  ml_user_id: string;
}

/** Busca a conexão ML do tenant (RLS limita ao que o usuário pode ver). */
function useMlConnection() {
  const [connection, setConnection] = useState<MlConnection | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    supabase
      .from('ml_connections')
      .select('nickname, ml_user_id')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setConnection((data as MlConnection | null) ?? null);
        setLoading(false);
      });
  }, []);
  return { connection, loading };
}

export function LogisticsDashboard() {
  const { connection, loading } = useMlConnection();
  const { orders, kpis, dailySales, topProducts } = useMlOrders(30);
  const { metrics } = useMlMetrics();

  const visits = metrics?.visits_total_30d ?? null;
  const conversionPct = visits && visits > 0 ? `${((kpis.count / visits) * 100).toFixed(2)}%` : '—';
  const paidCount = orders.filter((o) => ['paid', 'delivered', 'shipped'].includes(o.status)).length;

  return (
    <div className="space-y-6">
      {/* Banner de conexão — reflete o estado real em ml_connections */}
      {!loading && connection ? (
        <div className="rounded-2xl border border-positive/30 bg-positive/5 p-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-positive shrink-0" />
            <div>
              <p className="font-semibold text-ink">
                Mercado Livre conectado{connection.nickname ? ` — conta ${connection.nickname}` : ''}
              </p>
              <p className="text-sm text-ink-muted mt-0.5">
                Pedidos, repasses, estoque e atendimento serão sincronizados automaticamente.
              </p>
            </div>
          </div>
          <button onClick={connectML} className="btn-secondary shrink-0 gap-2">
            <ExternalLink className="w-4 h-4" />
            Reconectar
          </button>
        </div>
      ) : (
        <div className="rounded-2xl border border-brand-200 bg-brand-50 p-5 flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-brand-900">Conecte sua conta do Mercado Livre</p>
            <p className="text-sm text-brand-700 mt-0.5">
              Autorize o acesso para sincronizar pedidos, repasses, estoque e atendimento em tempo real.
            </p>
          </div>
          <button onClick={connectML} className="btn-primary shrink-0 gap-2">
            <ExternalLink className="w-4 h-4" />
            Conectar E-commerce
          </button>
        </div>
      )}

      {/* KPIs — calculados de ml_orders (últimos 30 dias) */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard label="Faturamento bruto" value={formatBRL(kpis.grossCents)} hint="Últimos 30 dias" icon={DollarSign} />
        <StatCard label="Faturamento líquido" value={formatBRL(kpis.netCents)} hint="Descontadas tarifas e frete" icon={BadgePercent} />
        <StatCard label="Pedidos" value={String(kpis.count)} hint="Últimos 30 dias" icon={ShoppingBag} />
        <StatCard label="Ticket médio" value={formatBRL(kpis.avgTicketCents)} icon={TicketCheck} />
        <StatCard label="Conversão" value={conversionPct} hint={visits ? `${visits.toLocaleString('pt-BR')} visitas em 30d` : 'Aguardando visitas'} icon={Eye} />
        <StatCard label="Reputação" value={reputationLabel(metrics?.reputation).label} hint="Mercado Livre" icon={Award} />
      </div>

      <SalesChart data={dailySales} />
      <OrdersTable orders={orders} />

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        <TopProducts products={topProducts} />
        <InventoryAlerts items={metrics?.low_stock_items} />
        <ConversionFunnel visits={visits} orders={kpis.count} paid={paidCount} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CustomerService questionsOpen={metrics?.questions_open} reputation={metrics?.reputation} />
          </div>
        </div>
  );
}
